package top.wcpe.allincore.ui.runtime.render.transform

import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmField
import kotlin.jvm.JvmName
import kotlin.math.abs

/**
 * 2D 仿射变换（平移 + 缩放）的不可变值对象。
 *
 * 提供给 `InputDispatcher`（命中测试，使用 float）与 `UIRenderer`（绘制坐标，亦使用 float）
 * 共享一致的 transform 数学：
 * ```
 *   T_new   = T_parent + S_parent * T_local
 *   S_new   = S_parent * S_local
 *   apply(x) = x * S + T
 * ```
 * 命中测试和裁剪都依赖这个组合公式；任何一处偏离都会导致输入与渲染错位。
 */
data class Transform2D(
    @get:JvmName("scaleX") val scaleX: Float,
    @get:JvmName("scaleY") val scaleY: Float,
    @get:JvmName("translateX") val translateX: Float,
    @get:JvmName("translateY") val translateY: Float,
) {

    /** 在当前 transform 之上叠加元素自身的 runtime transform，返回新的不可变 transform。 */
    fun push(element: UIElement<*>): Transform2D =
        push(element.translateX(), element.translateY(), element.scaleX(), element.scaleY())

    /** 在当前 transform 之上叠加给定的 (translate, scale)。 */
    fun push(tx: Float, ty: Float, sx: Float, sy: Float): Transform2D =
        Transform2D(scaleX * sx, scaleY * sy, translateX + scaleX * tx, translateY + scaleY * ty)

    /** 把局部 X 坐标变换到世界坐标系。 */
    fun applyX(x: Float): Float = x * scaleX + translateX

    /** 把局部 Y 坐标变换到世界坐标系。 */
    fun applyY(y: Float): Float = y * scaleY + translateY

    /**
     * 把局部矩形 (x,y,w,h) 变换到世界坐标系，返回 `[left, top, width, height, |scaleX|, |scaleY|]`。
     *
     * 处理负 scale：左上角取 min(x1,x2)，宽度取 |x2-x1|。
     */
    fun transformedBox(x: Float, y: Float, width: Float, height: Float): FloatArray {
        val x1 = applyX(x)
        val x2 = applyX(x + width)
        val y1 = applyY(y)
        val y2 = applyY(y + height)
        return floatArrayOf(
            minOf(x1, x2),
            minOf(y1, y2),
            abs(x2 - x1),
            abs(y2 - y1),
            abs(scaleX),
            abs(scaleY),
        )
    }

    companion object {
        /** 单位变换：scale=(1,1)，translate=(0,0)。 */
        @JvmField
        val IDENTITY = Transform2D(1f, 1f, 0f, 0f)
    }
}
